import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

// Admin liquidity-planning report: upcoming recurring + bulk scheduled debits,
// grouped by day and split by service type, for aggregator funding planning.
export default function LiquidityPlanning() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` },
  });

  const money = (n) =>
    `₦${Math.abs(Number(n) || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/admin/reports/liquidity-planning?days=${days}`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.status === 403 ? "Admin access required." : "Failed to load the liquidity report.");
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    try {
      const res = await api.get(
        `/admin/reports/liquidity-planning/export?days=${days}&format=${format}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `liquidity-planning-${new Date().toISOString().slice(0, 10)}.${format === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Export failed.");
    }
  };

  // ---- styles (inline, matching the plain admin pages) ----
  const S = {
    page: { padding: 24, maxWidth: 1200, margin: "0 auto", fontFamily: "inherit", color: "#1f2937" },
    h1: { fontSize: 22, fontWeight: 700, margin: 0 },
    sub: { color: "#6b7280", marginTop: 4, fontSize: 13 },
    bar: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "16px 0 20px" },
    select: { padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14 },
    btn: { padding: "8px 14px", border: "1px solid #1F4E78", background: "#1F4E78", color: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 14 },
    btnGhost: { padding: "8px 14px", border: "1px solid #1F4E78", background: "#fff", color: "#1F4E78", borderRadius: 8, cursor: "pointer", fontSize: 14 },
    kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 22 },
    card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, background: "#fff" },
    kLabel: { fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 },
    kValue: { fontSize: 22, fontWeight: 700, marginTop: 6 },
    sectionH: { fontSize: 15, fontWeight: 700, margin: "26px 0 10px" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" },
    th: { textAlign: "left", padding: "10px 12px", background: "#1F4E78", color: "#fff", fontWeight: 600, fontSize: 12.5 },
    thR: { textAlign: "right", padding: "10px 12px", background: "#1F4E78", color: "#fff", fontWeight: 600, fontSize: 12.5 },
    td: { padding: "9px 12px", borderTop: "1px solid #eef2f7" },
    tdR: { padding: "9px 12px", borderTop: "1px solid #eef2f7", textAlign: "right", whiteSpace: "nowrap" },
    pill: (ok) => ({ padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: ok ? "#dcfce7" : "#fee2e2", color: ok ? "#166534" : "#991b1b" }),
    cap: { textTransform: "capitalize" },
    err: { background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", padding: "10px 14px", borderRadius: 8, marginBottom: 16 },
  };

  const presets = [7, 14, 30, 60, 90];

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Liquidity Planning</h1>
      <div style={S.sub}>
        Upcoming recurring &amp; bulk scheduled debits projected across the window, split by service type,
        for aggregator funding. Face amount = aggregator funding need; face + fee = wallet debit.
      </div>

      <div style={S.bar}>
        <label style={{ fontSize: 13, color: "#374151" }}>Look-ahead:</label>
        <select style={S.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {presets.map((d) => (<option key={d} value={d}>{d} days</option>))}
        </select>
        <button style={S.btn} onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        <span style={{ flex: 1 }} />
        <button style={S.btnGhost} onClick={() => download("xlsx")}>Export Excel</button>
        <button style={S.btnGhost} onClick={() => download("csv")}>Export CSV</button>
      </div>

      {error && <div style={S.err}>{error}</div>}

      {data && (
        <>
          <div style={S.sub}>
            {data.fromDate} → {data.toDate} · {data.totalItems} scheduled debit(s) · {data.totalBeneficiaries} beneficiaries
          </div>

          <div style={S.kpis}>
            <div style={S.card}><div style={S.kLabel}>Aggregator funding (face)</div><div style={S.kValue}>{money(data.grandFace)}</div></div>
            <div style={S.card}><div style={S.kLabel}>Service fees</div><div style={S.kValue}>{money(data.grandFee)}</div></div>
            <div style={S.card}><div style={S.kLabel}>Total wallet debit</div><div style={S.kValue}>{money(data.grandDebit)}</div></div>
            <div style={S.card}><div style={S.kLabel}>Days with runs</div><div style={S.kValue}>{data.dayCount}</div></div>
          </div>

          {/* By service type = per-aggregator funding */}
          <div style={S.sectionH}>Funding by service type (per aggregator)</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Service type</th><th style={S.thR}>Items</th><th style={S.thR}>Beneficiaries</th>
              <th style={S.thR}>Face (funding)</th><th style={S.thR}>Fee</th><th style={S.thR}>Total debit</th>
            </tr></thead>
            <tbody>
              {(data.byServiceType || []).map((s) => (
                <tr key={s.serviceType}>
                  <td style={{ ...S.td, ...S.cap }}>{s.serviceType}</td>
                  <td style={S.tdR}>{s.items}</td>
                  <td style={S.tdR}>{s.beneficiaries}</td>
                  <td style={S.tdR}>{money(s.faceAmount)}</td>
                  <td style={S.tdR}>{money(s.serviceFee)}</td>
                  <td style={S.tdR}>{money(s.totalDebit)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...S.td, fontWeight: 700 }}>TOTAL</td>
                <td style={S.tdR}>{data.totalItems}</td>
                <td style={S.tdR}>{data.totalBeneficiaries}</td>
                <td style={{ ...S.tdR, fontWeight: 700 }}>{money(data.grandFace)}</td>
                <td style={{ ...S.tdR, fontWeight: 700 }}>{money(data.grandFee)}</td>
                <td style={{ ...S.tdR, fontWeight: 700 }}>{money(data.grandDebit)}</td>
              </tr>
            </tbody>
          </table>

          {/* Per-day funding need */}
          <div style={S.sectionH}>Daily funding need</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Run date</th><th style={S.thR}>Items</th><th style={S.thR}>Beneficiaries</th>
              <th style={S.thR}>Face (funding)</th><th style={S.thR}>Fee</th><th style={S.thR}>Total debit</th>
              <th style={S.th}>Service mix</th>
            </tr></thead>
            <tbody>
              {(data.days || []).map((d) => (
                <tr key={d.date}>
                  <td style={S.td}>{d.date}</td>
                  <td style={S.tdR}>{d.itemCount}</td>
                  <td style={S.tdR}>{d.beneficiaryCount}</td>
                  <td style={S.tdR}>{money(d.faceTotal)}</td>
                  <td style={S.tdR}>{money(d.feeTotal)}</td>
                  <td style={{ ...S.tdR, fontWeight: 600 }}>{money(d.debitTotal)}</td>
                  <td style={{ ...S.td, ...S.cap, fontSize: 12.5, color: "#6b7280" }}>
                    {(d.byServiceType || []).map((s) => `${s.serviceType}: ${money(s.faceAmount)}`).join("  ·  ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Payer shortfalls */}
          <div style={S.sectionH}>Payer wallet coverage</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Payer</th><th style={S.th}>Type</th>
              <th style={S.thR}>Scheduled debit</th><th style={S.thR}>Wallet balance</th>
              <th style={S.thR}>Shortfall</th><th style={S.th}>Status</th>
            </tr></thead>
            <tbody>
              {(data.payerSummary || []).map((p, i) => {
                const short = Number(p.projectedShortfall) > 0;
                return (
                  <tr key={i}>
                    <td style={S.td}>{p.payerName}</td>
                    <td style={S.td}>{p.payerType}</td>
                    <td style={S.tdR}>{money(p.scheduledDebit)}</td>
                    <td style={S.tdR}>{money(p.walletBalance)}</td>
                    <td style={{ ...S.tdR, color: short ? "#991b1b" : "#166534", fontWeight: 600 }}>{money(p.projectedShortfall)}</td>
                    <td style={S.td}><span style={S.pill(!short)}>{short ? "Needs funding" : "Covered"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {!data && !loading && !error && <div style={S.sub}>No data.</div>}
    </div>
  );
}
